import { Player } from '@lottiefiles/react-lottie-player';
import { Button, HyperLink, ImageAtoms } from '@vfit/shared/atoms';
import { requireStaticImageAtomComponent } from '@vfit/shared/data-access';
import { FrameDateInput, SlotsCalendarInput } from '../Inputs';
import StickyOffer from '../StickyOffer/stickyOffer';
import VfModal from '../VfModal/vfModal';
import { useSlotsTechAppointment } from './slotsTechAppointment.hook';
import { ISlotsTechAppointment } from './slotsTechAppointment.models';
import * as S from './slotsTechAppointment.style';
import { Skeleton } from '../Skeleton/Skeleton';

export const SlotsTechAppointment: React.FC<ISlotsTechAppointment> = ({
  calendarModalData,
  confirm,
  dateTimes,
  loading,
  loadingIcon,
  loadingLottie,
  showOtherAvailabilityLinkLabel,
  stickyOfferButtonLabel,
  track,
  variant,
}) => {
  const {
    close,
    confirmFromCalendarForm,
    confirmFromFilteredForm,
    filteredDateTimes,
    isOpen,
    isValidCalendarForm,
    isValidFilteredForm,
    open,
    orderedDateTimes,
    pNoOtherSlotsDescription,
    slotsGroupsMaxHeight,
    slotsTechAppointmentSchema,
    registerCalendarForm,
    registerFilteredForm,
    selectedFilteredAppointment,
  } = useSlotsTechAppointment({
    calendarModalButtonLabel: calendarModalData.buttonLabel,
    confirm,
    dateTimes,
    noOtherSlotsDescription: calendarModalData?.noOtherSlots?.description,
    loading,
    showOtherAvailabilityLinkLabel,
    stickyOfferButtonLabel,
    track,
  });

  return (
    <>
      <S.FilteredAppointments>
        {loading ? (
          <>
            <Skeleton {...S.skeletonStyle} />
            <Skeleton {...S.skeletonStyle} />
            <Skeleton {...S.skeletonStyle} />
          </>
        ) : (
          filteredDateTimes.map(({ id, validFor: { endDateTime, startDateTime } }) => (
            <FrameDateInput
              endDateTime={endDateTime}
              key={id}
              startDateTime={startDateTime}
              value={id}
              {...registerFilteredForm(
                'filteredAppointments',
                slotsTechAppointmentSchema.filteredAppointments
              )}
            />
          ))
        )}
      </S.FilteredAppointments>
      {showOtherAvailabilityLinkLabel && (
        <S.ShowOtherAvailability>
          <HyperLink size="big" onClick={open} disabled={loading}>
            {showOtherAvailabilityLinkLabel}
          </HyperLink>
        </S.ShowOtherAvailability>
      )}
      <StickyOffer
        isLoading={loading}
        id="sicky-offer-take-tech-appointment"
        topLabel={selectedFilteredAppointment.day}
        title={selectedFilteredAppointment.time}
        ctaActivation={{
          isDisabled: !isValidFilteredForm || loading || filteredDateTimes?.length === 0,
          action: confirmFromFilteredForm,
          label: stickyOfferButtonLabel,
        }}
      />
      <VfModal handleClose={close} height={590} isOpen={isOpen}>
        <>
          <S.CalendarModalTitle>{calendarModalData?.title}</S.CalendarModalTitle>
          {loading && (loadingIcon || loadingLottie) ? (
            <S.PlayerWrap>
              {!!loadingIcon && (
                <S.IconWrap>
                  <ImageAtoms nameCard="Icon" image={loadingIcon} />
                </S.IconWrap>
              )}
              {!!loadingLottie && (
                <Player
                  src={requireStaticImageAtomComponent(loadingLottie).src}
                  className="player"
                  loop
                  autoplay
                />
              )}
            </S.PlayerWrap>
          ) : (
            <>
              <S.CalendarAndNoSlotsBox>
                <SlotsCalendarInput
                  dateTimes={orderedDateTimes}
                  {...registerCalendarForm(
                    'calendarAppointments',
                    slotsTechAppointmentSchema.calendarAppointments
                  )}
                  slotsGroupsMaxHeight={slotsGroupsMaxHeight}
                >
                  <S.NoOtherSlotsTitle>
                    {calendarModalData?.noOtherSlots?.title}
                  </S.NoOtherSlotsTitle>
                  {pNoOtherSlotsDescription && (
                    <S.NoOtherSlotsDescription
                      dangerouslySetInnerHTML={{
                        __html: pNoOtherSlotsDescription,
                      }}
                    />
                  )}
                </SlotsCalendarInput>
              </S.CalendarAndNoSlotsBox>
              <S.CalendarModalFooter>
                <Button
                  disabled={!isValidCalendarForm || loading}
                  onClick={confirmFromCalendarForm}
                  variant={variant}
                >
                  {calendarModalData?.buttonLabel}
                </Button>
              </S.CalendarModalFooter>
            </>
          )}
        </>
      </VfModal>
    </>
  );
};

